import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Badge, SimpleGrid, Anchor } from '@repletoryreach/sdk-ui';
import { SegmentedControl, Switch } from '@repletoryreach/sdk-ui/inputs';
import { Alert, EmptyState, PageHeader, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess, notifyWarning } from '@repletoryreach/sdk-ui/notifications';
import { StatCard } from '../../../components/StatCard';
import { TableFooter } from '../../../components/TableFooter';
import { IconAlertFile, IconClock, IconGlobe, IconTag } from '../components/icons';
import { useMarkAllMetaNewsRead, useMarkMetaNews, useMetaNews, useMetaNewsSources, useMetaNewsSummary, useRefreshMetaNews } from '../../../data/useMetaNews';
import { formatDate, formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useT } from '../../../i18n/I18nProvider';
import { SourcesCard } from './components/SourcesCard';
import '../components/billing.css';
import './meta-updates.css';

const STAT_COLS = { base: 1, sm: 2, lg: 4 };

/** The filters the owner reaches for; policy items still show under All. */
const TOPIC_FILTERS = ['all', 'pricing', 'whatsapp', 'api', 'templates'] as const;

/**
 * Meta updates: what Meta changes on the WhatsApp Business Platform, collected
 * by the settings service from Meta's pricing page, its changelog and its
 * developer blog. Pricing opens first, because a new rate card is the change
 * that has to reach our SKU rates before it takes effect.
 */
export default function MetaUpdatesPage() {
  const t = useT();
  const navigate = useNavigate();
  const [topic, setTopic] = usePersistedState<string>('admin.meta-updates.topic', 'pricing');
  const [unreadOnly, setUnreadOnly] = usePersistedState<boolean>('admin.meta-updates.unreadOnly', false);
  const [pageSize, setPageSize] = usePersistedState<number>('admin.meta-updates.pageSize', 25);
  const [page, setPage] = useState(1);

  const listOpts = useMemo<admin.ListMetaNewsOptions>(() => ({
    topic: topic === 'all' ? undefined : (topic as admin.MetaNewsTopic),
    unread: unreadOnly ? true : undefined,
    page,
    page_size: pageSize
  }), [topic, unreadOnly, page, pageSize]);

  const itemsQ = useMetaNews(listOpts);
  const summaryQ = useMetaNewsSummary();
  const sourcesQ = useMetaNewsSources();
  const refresh = useRefreshMetaNews();
  const mark = useMarkMetaNews();
  const markAll = useMarkAllMetaNewsRead();

  const items = itemsQ.data?.data ?? [];
  const total = itemsQ.data?.meta.total ?? 0;
  const summary = summaryQ.data?.data;
  const sources = sourcesQ.data?.data ?? [];
  const enabledSources = sources.filter((s) => s.enabled).length;
  const failingSources = sources.filter((s) => s.enabled && s.last_error).length;
  const unreadPricing = summary?.unread_pricing_count ?? 0;
  const lastChecked = summary?.last_checked_at ?? null;
  const filtersActive = topic !== 'all' || unreadOnly;

  const topicLabel = (code: string) => t(`billing.metaUpdates.topic.${code}`);

  function checkNow() {
    refresh.mutate(undefined, {
      onSuccess: (res) => {
        const results = res.data;
        const added = results.reduce((sum, r) => sum + r.new_items, 0);
        const failed = results.filter((r) => !r.ok).length;
        notifySuccess(t('billing.metaUpdates.toast.checked', { sources: formatInteger(results.length), count: formatInteger(added) }), t('billing.metaUpdates.title'));
        if (failed > 0) notifyWarning(t('billing.metaUpdates.toast.someFailed', { count: formatInteger(failed) }), t('billing.metaUpdates.title'));
      },
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.metaUpdates.title'))
    });
  }

  function toggleRead(item: admin.MetaNewsItem) {
    mark.mutate({ id: item.id, read: item.unread }, {
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.metaUpdates.title'))
    });
  }

  function confirmMarkAll() {
    const scoped = topic !== 'all';
    openConfirmModal({
      title: scoped ? t('billing.metaUpdates.confirm.markAllTopicTitle', { topic: topicLabel(topic) }) : t('billing.metaUpdates.confirm.markAllTitle'),
      children: <Text role="bodySmall">{t('billing.metaUpdates.confirm.markAllBody')}</Text>,
      labels: { confirm: t('billing.metaUpdates.action.markAllRead'), cancel: t('action.cancel') },
      onConfirm: () => markAll.mutate(scoped ? (topic as admin.MetaNewsTopic) : undefined, {
        onSuccess: (res) => notifySuccess(t('billing.metaUpdates.toast.markedAll', { count: formatInteger(res.data.updated) }), t('billing.metaUpdates.title')),
        onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.metaUpdates.title'))
      })
    });
  }

  function clearFilters() {
    setTopic('all');
    setUnreadOnly(false);
    setPage(1);
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.metaUpdates.eyebrow')}
        title={t('billing.metaUpdates.title')}
        description={t('billing.metaUpdates.description')}
        actions={
          <Group gap="sm">
            <span title={lastChecked ? formatDateTime(lastChecked) : undefined}>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {lastChecked ? t('billing.metaUpdates.lastChecked', { when: formatRelative(lastChecked) }) : t('billing.metaUpdates.neverChecked')}
              </Text>
            </span>
            <Button size="sm" onClick={checkNow} disabled={refresh.isPending}>
              {refresh.isPending ? t('billing.metaUpdates.action.checking') : t('billing.metaUpdates.action.checkNow')}
            </Button>
          </Group>
        }
      />

      {unreadPricing > 0 && (
        <Alert color="warning" title={t('billing.metaUpdates.callout.title', { count: formatInteger(unreadPricing) })}>
          <Stack gap="sm">
            <Text role="bodySmall">{t('billing.metaUpdates.callout.body')}</Text>
            {summary?.latest_pricing_item && (
              <Text role="bodySmall">
                {t('billing.metaUpdates.callout.latest')}{' '}
                <Anchor href={summary.latest_pricing_item.url} target="_blank">{summary.latest_pricing_item.title}</Anchor>
              </Text>
            )}
            <Group gap="sm">
              <Button size="xs" onClick={() => navigate('/billing/sku-rates')}>{t('billing.metaUpdates.callout.action')}</Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {summaryQ.error ? (
        <Alert color="error">
          <Stack gap="sm">
            <Text role="bodySmall">{t('billing.metaUpdates.summaryError')}</Text>
            <Group><Button size="xs" variant="outline" onClick={() => void summaryQ.refetch()}>{t('action.retry')}</Button></Group>
          </Stack>
        </Alert>
      ) : (
        <SimpleGrid cols={STAT_COLS} spacing="md">
          <StatCard
            withRule tone="info" icon={<IconAlertFile />}
            label={t('billing.metaUpdates.stat.unread')}
            value={formatInteger(summary?.unread_count ?? 0)}
            hint={t('billing.metaUpdates.stat.unreadHint')}
            loading={summaryQ.isLoading}
          />
          <StatCard
            withRule tone={unreadPricing > 0 ? 'warning' : 'success'} icon={<IconTag />}
            label={t('billing.metaUpdates.stat.pricing')}
            value={formatInteger(unreadPricing)}
            hint={t('billing.metaUpdates.stat.pricingHint')}
            loading={summaryQ.isLoading}
          />
          <StatCard
            withRule tone={failingSources > 0 ? 'error' : 'primary'} icon={<IconGlobe />}
            label={t('billing.metaUpdates.stat.sources')}
            value={formatInteger(enabledSources)}
            hint={failingSources > 0
              ? t('billing.metaUpdates.stat.sourcesFailing', { count: formatInteger(failingSources) })
              : t('billing.metaUpdates.stat.sourcesHint', { enabled: formatInteger(enabledSources), total: formatInteger(sources.length) })}
            loading={sourcesQ.isLoading}
          />
          <StatCard
            withRule tone="neutral" icon={<IconClock />}
            label={t('billing.metaUpdates.stat.lastChecked')}
            value={lastChecked ? formatRelative(lastChecked) : t('common.never')}
            hint={t('billing.metaUpdates.stat.lastCheckedHint')}
            loading={summaryQ.isLoading}
          />
        </SimpleGrid>
      )}

      <Group gap="sm" justify="space-between" wrap="wrap">
        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            size="xs"
            value={topic}
            onChange={(v) => { setTopic(v); setPage(1); }}
            data={TOPIC_FILTERS.map((code) => ({ value: code, label: topicLabel(code) }))}
          />
          <Switch
            size="xs"
            checked={unreadOnly}
            onChange={(v) => { setUnreadOnly(v); setPage(1); }}
            label={t('billing.metaUpdates.unreadOnly')}
          />
        </Group>
        <Button size="xs" variant="outline" onClick={confirmMarkAll} disabled={markAll.isPending || (summary?.unread_count ?? 0) === 0}>
          {t('billing.metaUpdates.action.markAllRead')}
        </Button>
      </Group>

      <TableShell
        totalLabel={t('billing.metaUpdates.showingCount', { shown: formatInteger(items.length), total: formatInteger(total) })}
        loading={itemsQ.isLoading}
        isEmpty={!itemsQ.isLoading && items.length === 0}
        empty={
          <EmptyState
            title={unreadOnly ? t('billing.metaUpdates.emptyUnread') : t('billing.metaUpdates.empty')}
            description={filtersActive ? t('common.tryClearingFilters') : t('billing.metaUpdates.emptyHint')}
            action={filtersActive
              ? <Button size="xs" variant="outline" onClick={clearFilters}>{t('common.clearFilters')}</Button>
              : <Button size="xs" onClick={checkNow} disabled={refresh.isPending}>{t('billing.metaUpdates.action.checkNow')}</Button>}
          />
        }
        error={itemsQ.error ? (
          <Alert color="error">
            <Stack gap="sm">
              <Text role="bodySmall">{t('billing.metaUpdates.loadError')}</Text>
              <Group><Button size="xs" variant="outline" onClick={() => void itemsQ.refetch()}>{t('action.retry')}</Button></Group>
            </Stack>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        <ul className="rr-meta-updates__list">
          {items.map((item) => {
            const date = item.published_at ?? item.discovered_at;
            return (
              <li key={item.id} className={item.unread ? 'rr-meta-updates__item is-unread' : 'rr-meta-updates__item'}>
                <span className="rr-meta-updates__marker" aria-hidden />
                <div className="rr-meta-updates__body">
                  <div className="rr-meta-updates__head">
                    <Stack gap="xs">
                      <Group gap="xs" wrap="wrap">
                        {item.unread && <Badge size="xs" variant="filled" color="blue">{t('billing.metaUpdates.item.unread')}</Badge>}
                        <Anchor className="rr-meta-updates__title" href={item.url} target="_blank">{item.title}</Anchor>
                      </Group>
                      <span title={formatDateTime(date)}>
                        <Text role="caption" c="var(--rr-color-text-muted)">
                          {item.published_at
                            ? t('billing.metaUpdates.item.meta', { source: item.source_name, date: formatDate(date) })
                            : t('billing.metaUpdates.item.metaFound', { source: item.source_name, date: formatDate(date) })}
                        </Text>
                      </span>
                    </Stack>
                    <Button size="xs" variant="subtle" onClick={() => toggleRead(item)} disabled={mark.isPending && mark.variables?.id === item.id}>
                      {item.unread ? t('billing.metaUpdates.action.markRead') : t('billing.metaUpdates.action.markUnread')}
                    </Button>
                  </div>
                  <Text role="bodySmall" lineClamp={4} className="rr-meta-updates__summary" c={item.summary ? undefined : 'var(--rr-color-text-muted)'}>
                    {item.summary || t('billing.metaUpdates.item.noSummary')}
                  </Text>
                  {item.topics.length > 0 && (
                    <Group gap="xs" wrap="wrap">
                      {item.topics.map((code) => (
                        <Badge key={code} size="xs" variant="light" color={code === 'pricing' ? 'orange' : 'gray'}>{topicLabel(code)}</Badge>
                      ))}
                    </Group>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </TableShell>

      <SourcesCard query={sourcesQ} />
    </Stack>
  );
}
